import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ScoreBadge } from "@/components/ScoreBadge";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";

export default function ComparisonDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview");
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null); setError(false);
    api.get("/scoring-config").then((r) => setCategories(r.data.categories));
    api.get(`/articles/${slug}${preview ? "?preview=1" : ""}`).then((r) => setArticle(r.data)).catch(() => setError(true));
  }, [slug, preview]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><Seo title="Not found" noindex /><p className="font-serif-display text-4xl font-black">Not found.</p><Link to="/comparisons" className="inline-block mt-6 font-mono-pm text-sm underline">← Comparisons</Link></div>;
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-24 font-mono-pm text-sm text-stone-500">Loading…</div>;

  const products = article.compare_products || [];
  const date = (article.last_reviewed_at || article.updated_at || "").slice(0, 10);

  return (
    <article>
      <Seo title={article.meta_title || article.title} description={article.meta_description || article.excerpt}
        image={article.featured_image ? assetUrl(article.featured_image) : undefined}
        noindex={article.status !== "published"} type="article" />

      {article.status !== "published" && (
        <div className="bg-[#991B1B] text-white text-center py-2 font-mono-pm text-xs tracking-widest uppercase" data-testid="preview-banner">
          Preview — {article.status} (not public, noindex)
        </div>
      )}

      <header className="border-b-2 border-stone-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
          <Link to="/comparisons" className="font-mono-pm text-[10px] tracking-[0.25em] uppercase text-[#991B1B] hover:underline">← Comparisons</Link>
          <p className="font-mono-pm text-xs tracking-widest uppercase text-stone-400 mt-4">Head-to-Head</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black leading-tight text-stone-900 mt-2">{article.title}</h1>
          {article.excerpt && <p className="text-xl text-stone-600 mt-4">{article.excerpt}</p>}
          {date && <p className="font-mono-pm text-xs text-stone-500 mt-6">Last updated {date}</p>}
        </div>
      </header>

      {article.featured_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-10">
          <img src={assetUrl(article.featured_image)} alt={article.featured_image_alt || article.title}
            data-testid="comparison-featured-image" className="w-full h-auto border-2 border-stone-900" />
        </div>
      )}

      {products.length >= 2 ? (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 overflow-x-auto">
          <table className="w-full border-collapse min-w-[600px]" data-testid="comparison-table">
            <thead>
              <tr>
                <th className="border border-stone-900 bg-stone-900 text-white p-4 text-left font-mono-pm text-xs uppercase tracking-widest sticky left-0">Category</th>
                {products.map((p) => (
                  <th key={p.slug} className="border border-stone-900 bg-stone-900 text-white p-4 text-center align-top">
                    <div className="font-serif-display text-lg font-bold mb-3">{p.product_name || p.title}</div>
                    <div className="flex justify-center"><ScoreBadge score={p.overall_score} passes={p.passes} size="sm" unscored={!p.is_scored} /></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((c, ri) => (
                <tr key={c.key} className={ri % 2 ? "bg-[#F2F2F0]" : "bg-white"}>
                  <td className="border border-stone-900 p-3 font-mono-pm text-xs uppercase tracking-wide text-stone-700 sticky left-0 bg-inherit">{c.label}</td>
                  {products.map((p) => {
                    const v = p.scores?.[c.key];
                    return <td key={p.slug} className="border border-stone-900 p-3 text-center font-mono-pm font-bold text-lg text-stone-900">{v != null && v !== "" ? v : "—"}</td>;
                  })}
                </tr>
              ))}
              <tr className="bg-stone-900 text-white">
                <td className="border border-stone-900 p-3 font-mono-pm text-xs uppercase tracking-widest sticky left-0 bg-stone-900">Overall / 100</td>
                {products.map((p) => (
                  <td key={p.slug} className="border border-stone-900 p-3 text-center font-mono-pm font-black text-2xl text-[#CCFF00]">{p.overall_score ?? "—"}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="mt-8 flex flex-wrap gap-4">
            {products.map((p) => (
              <Link key={p.slug} to={`/reviews/${p.slug}`} className="font-mono-pm text-xs tracking-widest uppercase bg-stone-900 text-white px-5 py-3 hover:bg-[#991B1B] transition-colors">
                Read {p.product_name || p.title} review →
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
          <div className="border-2 border-dashed border-stone-300 bg-white p-10 text-center">
            <p className="font-serif-display text-2xl text-stone-900">Comparison in progress.</p>
            <p className="font-mono-pm text-sm text-stone-500 mt-3">The products in this head-to-head are still being reviewed. We publish comparison scores only after each product's full review is complete.</p>
          </div>
        </section>
      )}

      {article.body && (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
          <div className="pm-prose" dangerouslySetInnerHTML={{ __html: article.body }} />
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10"><AffiliateDisclosure /></section>
    </article>
  );
}
